import { Component, EventEmitter, Input, Output } from '@angular/core';
import { ProdutoPromocional } from '../../../core/models/produtos/produto-promocional';
import { ProdutoTabela } from '../../../core/models/produtos/produto-tabela';
import { Configuracao } from '../../../core/models/configuracoes/configuracao';
import { PrazoPagamento } from '../../../core/models/prazos-pagamento/prazo-pagamento';

/**
 * Conteúdo (corpo) do `<ng-template #adicionarItensTabelas let-modal>` do componente pai
 * (pedido-adicionar-itens-modal.component.html:342-450 na árvore atual — os limites citados no
 * brief original, linha 1704, estavam defasados pelas extrações das Tasks 3-9; reancorado via
 * `grep -n "adicionarItensTabelas"`. Esta é a última `<ng-template>` do arquivo, termina no EOF).
 *
 * Escopo: SOMENTE o `modal-body` (grid de tabelas promocionais/especiais do produto
 * selecionado, com colunas condicionais de fator financeiro). O `<ng-template>`,
 * `#adicionarItensTabelas`, `modal-header` (343-349), `modal-footer` (447-449) e o método
 * abridor `modalAdicionarItensTabelas(content, id_produto, id_lote)` (ts:5531) permanecem
 * intocados no pai — o botão Cancelar do rodapé só chama `modal.close(...)`, não valia a pena
 * extrair. `@ViewChild("adicionarItensTabelas") adicionarItensTabelasTemplate` (ts:294)
 * continua funcionando sem alteração pois aponta para o próprio `<ng-template>`, que não se move.
 *
 * `aplicarPrecoPromocionalETRavar(id_produto: number, preco: number, produtoTabela: any,
 * forcarFator: boolean = false)` (ts:2481) — assinatura grep-verificada — aplica o preço da
 * tabela com `bypass_desconto_maximo=true` e trava o preço (via `fixarEstadoPreco`) quando
 * `configuracao.tabelalistaoupromocao == "L"`; essa decisão de travar é 100% interna ao método
 * do pai, nada aqui precisa saber ou decidir sobre trava. O template original tem DOIS call
 * sites distintos (html:419 sem fator financeiro / html:434 com fator financeiro forçado —
 * mesmo produto/preço, só muda o último argumento), preservados aqui como dois `@Output`
 * separados (em vez de emitir o boolean como dado) para que o literal `false`/`true` continue
 * decidido estaticamente na chamada, igual ao original.
 */
@Component({
  selector: 'app-pedido-itens-modal-tabelas',
  standalone: false,
  templateUrl: './pedido-itens-modal-tabelas.component.html',
  styleUrls: ['./pedido-itens-modal-tabelas.component.scss']
})
export class PedidoItensModalTabelasComponent {
  /* Produto selecionado — mesma referência do pai (ts:185: `produtoTabelasSelecionado: ProdutoPromocional = null`). */
  @Input() produtoTabelasSelecionado: ProdutoPromocional = null;

  /* Lista de tabelas promocionais/especiais do produto (ts:184: `produtoTabelas: ProdutoTabela[] = []`). */
  @Input() produtoTabelas: ProdutoTabela[] = [];

  /* Contexto de cálculo (fator financeiro / pontuação) — mesmos Inputs já usados nas Tasks 8/9. */
  @Input() configuracao: Configuracao = null;
  @Input() prazoPagamentoSelecionado: PrazoPagamento = null;
  @Input() exibirPontuacao = false;

  /** Call site html:419 — coluna "Unitário: R$" clicável quando a coluna de fator NÃO é exibida
   *  (sem fator financeiro em jogo). Emite os mesmos 3 dados que o pai precisa; `forcarFator` é
   *  sempre `false` neste botão no original, então fica fixo na chamada do pai, não no evento. */
  @Output() precoAplicadoSemFator = new EventEmitter<{ idProduto: number; preco: number; produtoTabela: ProdutoTabela }>();

  /** Call site html:434 — coluna "C/ Fator: R$" clicável, exibida só quando
   *  `configuracao.utiliza_fator_financeiro` etc. `forcarFator` é sempre `true` neste botão no
   *  original. */
  @Output() precoAplicadoComFator = new EventEmitter<{ idProduto: number; preco: number; produtoTabela: ProdutoTabela }>();

  /** Corpo real verificado em pedido-adicionar-itens-modal.component.ts:5234-5261 — copiado
   *  verbatim (mesmo precedente de catalogo/detalhe-painel, Tasks 3/5). Função pura, sem efeito
   *  colateral no pai, usada em várias condições do template a cada render. */
  formatarValoresQuatroCasasDecimais(valor: number, porcentagem?: boolean, zero?: boolean): string {
    if (valor != null) {
      if (porcentagem) {
        return (valor).toLocaleString('pt-BR', {
          minimumFractionDigits: 4,
          maximumFractionDigits: 4
        });
      } else {
        return (valor).toLocaleString('pt-BR', {
          style: 'currency',
          currency: 'BRL',
          minimumFractionDigits: 4,
          maximumFractionDigits: 4
        });
      }
    } else {
      if (zero) {
        return (0).toLocaleString('pt-BR', {
          style: 'currency',
          currency: 'BRL',
          minimumFractionDigits: 4,
          maximumFractionDigits: 4
        });
      }

      return null;
    }
  }
}
